'use client';

import { type FC, useEffect } from 'react';
import { BaseFillExclamation } from '@/app/components/icons/BaseFillExclamation';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { cn, getSubscriptionUrl, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';

interface RetentionBannerProps {
  onClose: () => void;
  className?: string;
  phase: 'expiring' | 'lastDay';
}

const PHASE_CONFIG = {
  expiring: {
    warningText: '您已关闭自动续费，会员将在到期后结束',
    promoText: '限时续费或升级连续包月套餐享 8 折折扣',
  },
  lastDay: {
    warningText: '今日到期',
    promoText: '最后一天享 8 折续费，过期将降为免费版',
  },
} as const;

export const RetentionBanner: FC<RetentionBannerProps> = ({ onClose, className, phase }) => {
  const router = useRouter();
  const config = PHASE_CONFIG[phase];
  const trackingType = phase === 'lastDay' ? 'd1-topbanner' : 'd7-topbanner';

  useEffect(() => {
    reportEvent('page_name', { subscription_reactivation: trackingType });
  }, [trackingType]);

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };

  const handleClick = () => {
    reportEvent('subscription_reactivation_click', { view: trackingType });
    router.push(getSubscriptionUrl(false, 'tab=plan&subscription-period=subscription_monthly'));
  };

  return (
    <div
      className={cn(
        'relative flex h-12 w-full cursor-pointer items-center justify-between bg-[rgba(87,101,242,0.4)] px-8',
        className,
      )}
      onClick={handleClick}
    >
      <div className="flex flex-1 items-center justify-center gap-6">
        <div className="flex items-center gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <BaseFillExclamation className="size-4 text-[#96A0FF]" accentColor="#000" />
            <span className="whitespace-nowrap text-sm text-white leading-[1.4]">{config.warningText}</span>
          </div>
          <div className="h-[14px] w-px bg-white/20" />
          <span className="whitespace-nowrap text-sm text-white leading-[1.4]">{config.promoText}</span>
        </div>

        <button
          type="button"
          className="shrink-0 rounded-[10px] border border-system-white12 bg-black px-5 py-1 font-semibold text-white text-xs leading-[1.4] backdrop-blur-[2px]"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          继续订阅
        </button>
      </div>

      <button
        type="button"
        onClick={handleClose}
        className="shrink-0 text-white transition-colors hover:text-white/80"
        aria-label="Close"
      >
        <BaseLineClose className="size-4" />
      </button>
    </div>
  );
};
